import { type RefObject, useEffect } from 'react';

/**
 * Shared overlay behaviour for the language modal and the mega menu.
 *
 * Both need the same four things, and both would get them subtly wrong if
 * written twice: dismiss on Escape, dismiss on an outside click, keep Tab
 * inside while open, and put focus back where it came from on close. The last
 * is the one most often missed — without it, closing a dialog drops a keyboard
 * user at the top of the document, and they have to tab back to where they
 * were.
 */

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function focusableWithin(container: HTMLElement): readonly HTMLElement[] {
  // `offsetParent` filters out anything hidden by an ancestor, which a plain
  // selector match does not — a tab stop inside a collapsed panel is a hole
  // the user falls into with no visible focus ring.
  return [...container.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((element) => {
    // `checkVisibility` answers the real question — is this element rendered —
    // where `offsetParent` also returns null inside any fixed-position
    // subtree, which is every overlay this hook serves.
    if (typeof element.checkVisibility === 'function') {
      return element.checkVisibility({ checkOpacity: false, checkVisibilityCSS: true });
    }
    return element.offsetParent !== null || getComputedStyle(element).position === 'fixed';
  });
}

export interface OverlayOptions {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly containerRef: RefObject<HTMLElement | null>;
  /**
   * Focused on open. Falls back to the first focusable element, so a
   * forgotten ref degrades to something sensible rather than to nothing.
   */
  readonly initialFocusRef?: RefObject<HTMLElement | null>;
  /** Off for a popover anchored to its trigger, where a click elsewhere is a dismiss. */
  readonly trapFocus?: boolean;
}

export function useOverlay({
  open,
  onClose,
  containerRef,
  initialFocusRef,
  trapFocus = true,
}: OverlayOptions): void {
  useEffect(() => {
    if (!open) return;

    const container = containerRef.current;
    const previouslyFocused = document.activeElement as HTMLElement | null;

    // Deferred a frame: on open the element may not be laid out yet, and
    // focusing something with no box silently does nothing.
    const focusTimer = window.setTimeout(() => {
      const target = initialFocusRef?.current ?? (container ? focusableWithin(container)[0] : null);
      target?.focus();
    }, 0);

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
        return;
      }

      if (event.key !== 'Tab' || !trapFocus || container === null) return;

      const stops = focusableWithin(container);
      const first = stops[0];
      const last = stops[stops.length - 1];
      if (first === undefined || last === undefined) return;

      const active = document.activeElement;

      // Wrap at both ends, and recover if focus is already outside.
      //
      // The recovery case is not hypothetical. Tabbing past the overlay's last
      // stop moves focus into the browser's own chrome, and the next Tab lands
      // on the page behind — at which point `active === last` is never true
      // again and a trap written only around the two ends never fires. Once
      // focus is outside, the next Tab pulls it back to the appropriate end.
      const outside = !container.contains(active);

      if (event.shiftKey && (active === first || outside)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || outside)) {
        event.preventDefault();
        first.focus();
      }
    };

    const onPointerDown = (event: MouseEvent): void => {
      if (container?.contains(event.target as Node) === true) return;
      onClose();
    };

    document.addEventListener('keydown', onKeyDown, true);
    document.addEventListener('mousedown', onPointerDown);

    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener('keydown', onKeyDown, true);
      document.removeEventListener('mousedown', onPointerDown);
      // The overlay unmounts before this cleanup runs, so asking whether the
      // container still holds focus always answers no — focus has already
      // fallen back to `body`. That fallback is exactly the signal to restore:
      // it means the teardown dropped it rather than something else claiming
      // it. If focus did move somewhere real, leave it there.
      const lost = document.activeElement === null || document.activeElement === document.body;
      if (lost && previouslyFocused?.isConnected === true) previouslyFocused.focus();
    };
  }, [open, onClose, containerRef, initialFocusRef, trapFocus]);
}

/**
 * Stops the page scrolling behind a full-screen overlay.
 *
 * The padding compensation matters: removing the scrollbar reflows the whole
 * page a few pixels wider, which reads as the layout jumping when the menu
 * opens. Separate from `useOverlay` because a popover should not do it.
 */
export function useScrollLock(locked: boolean): void {
  useEffect(() => {
    if (!locked) return;

    const { body } = document;
    const previousOverflow = body.style.overflow;
    const previousPadding = body.style.paddingRight;
    const scrollbar = window.innerWidth - document.documentElement.clientWidth;

    body.style.overflow = 'hidden';
    if (scrollbar > 0) body.style.paddingRight = `${scrollbar}px`;

    return () => {
      body.style.overflow = previousOverflow;
      body.style.paddingRight = previousPadding;
    };
  }, [locked]);
}
